import React from 'react'
import withSchemeServiceAreas, {
  SERVICE_AREAS_GEOJSON,
} from '~/components/Scheme/withSchemeServiceAreas'
import Map, { ViewState } from '~/components/Map'
import {
  ServiceAreaGeoJsonLayer,
  ZoneIconLayers,
  ZonePolygonLayer,
  VehicleIconLayer,
} from '~/components/Map/layers'

import { MODULES_MAP_all_zones_nodes as ZONE } from '~/components/MapView/__generated__/MODULES_MAP'

import { LocationAreaInput } from '../../__generated__/globalTypes'
import { useZoneMapQuery, Vehicle } from '~/gql/generated/graphql'
import Legend, { LegendProps } from '~/components/Map/Legend'
import Loading from '~/components/Loading'
import { message } from 'antd'

type ZoneMapProps = {
  id: string
  serviceAreas: SERVICE_AREAS_GEOJSON
}

type ZoneMapState = {
  bounds: LocationAreaInput | null
  legendProps: LegendProps | null
}

const initialState: ZoneMapState = {
  bounds: null,
  legendProps: null,
}

const ZoneMap = ({ id, serviceAreas }: ZoneMapProps) => {
  const [state, setState] = React.useState<ZoneMapState>(initialState)
  const _setState = (newState: Partial<ZoneMapState>) =>
    setState({ ...state, ...newState })

  const { legendProps, bounds } = state

  const { loading, error, data, previousData } = useZoneMapQuery({
    variables: {
      id,
      zone_id: id,
      hasLocation: bounds !== null,
      location: bounds,
    },
  })

  if (loading && !previousData) {
    return <Loading />
  }

  if (error) {
    message.error(error.message)
    return null
  }

  const _onViewStateChange = (viewState: ViewState) => {
    _setState({
      bounds: {
        longitude_min: viewState.bounds._ne.lng,
        longitude_max: viewState.bounds._sw.lng,
        latitude_min: viewState.bounds._ne.lat,
        latitude_max: viewState.bounds._sw.lat,
      },
    })
  }

  const _setLegend = (legendProps: ZoneMapState['legendProps']) => {
    _setState({
      legendProps,
    })
  }

  const displayedData = data ?? previousData

  const currentZone = displayedData?.zone
  const vehicles = displayedData?.zone?.all_commissioned_vehicles as Vehicle[]
  const zones = displayedData?.zones?.nodes as ZONE[]
  return (
    <Map
      latitude={displayedData?.zone?.centre?.coordinates[1]}
      longitude={displayedData?.zone?.centre?.coordinates[0]}
      zoom={17}
      layers={[
        ServiceAreaGeoJsonLayer({
          serviceAreas,
          layerId: 'service-area-layer',
          layerProps: {
            onClick: ({ object: { properties } }) =>
              _setLegend({
                type: 'ServiceArea',
                service_area: properties,
              }),
          },
        }),
        ZonePolygonLayer({
          zones: currentZone ? [currentZone] : [],
          layerId: 'current-zone-polygon-layer',
          visible: true,
          visibleZoneId: id,
        }),
        VehicleIconLayer({
          vehicles,
          layerId: 'vehicles-icon-layer',
          layerProps: {
            getSize: 33,
            onClick: ({ object }) =>
              _setLegend({
                type: 'Vehicle',
                module_id: object?.detail?.module?.id,
              }),
          },
        }),
        ...ZoneIconLayers({
          zones,
          layerId: 'zone-layer',
          iconLayerProps: {
            onClick: ({ object }) =>
              _setLegend({ type: 'Zone', zone_id: object.id }),
          },
        }),
        ZoneIconLayers({
          zones: currentZone ? [currentZone] : [],
          layerId: 'current-zone-layer',
          iconLayerProps: {
            onClick: ({ object }) =>
              _setLegend({ type: 'Zone', zone_id: object.id }),
          },
        }),
      ]}
      onViewStateChange={_onViewStateChange}
      hideGeocoder
    >
      {legendProps && (
        <Legend onClose={() => _setLegend(null)} {...legendProps} />
      )}
    </Map>
  )
}

export default withSchemeServiceAreas(ZoneMap)
